// A chat log with its line field: speech bubbles (mine on the right), system lines centred in the
// faint voice, the log pinned to the newest line unless the reader has scrolled up. Data-free: the
// caller maps its rows to `ChatLine`s and handles `onSend` (docs/17 §5).
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Input } from './Form';
import { EmptyState } from './Card';
import { playSfx } from '../../core/audio';

export interface ChatLine { key: string; who?: string; time?: string; body: string; me?: boolean; system?: boolean }

export interface ChatLogProps {
  lines: ChatLine[];
  onSend?: (body: string) => Promise<boolean> | boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Shown when there are no lines. */
  empty?: ReactNode;
  label?: string;
  className?: string;
  /** Test ids for the log, the field and the send button. */
  testIds?: { log?: string; input?: string; send?: string };
  autoFocus?: boolean;
}

export function ChatLog({ lines, onSend, placeholder = 'say something', disabled, empty, label = 'Chat', className = '', testIds = {}, autoFocus }: ChatLogProps) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const logRef = useRef<HTMLOListElement>(null);
  const pinned = useRef(true);
  useLayoutEffect(() => { const el = logRef.current; if (el && pinned.current) el.scrollTop = el.scrollHeight; }, [lines.length]);
  useEffect(() => { const el = logRef.current; if (el) el.scrollTop = el.scrollHeight; }, []);
  // docs/34: someone else's line arriving chimes (mine and the system's are silent)
  const seen = useRef(lines.length);
  useEffect(() => {
    if (lines.length > seen.current) { const last = lines[lines.length - 1]; if (last && !last.me && !last.system) playSfx('ui.chat'); }
    seen.current = lines.length;
  }, [lines]);
  const send = async () => {
    const body = draft.trim();
    if (!body || !onSend || busy) return;
    setBusy(true);
    const ok = await onSend(body);
    setBusy(false);
    if (ok) { setDraft(''); pinned.current = true; }
  };
  return (
    <div className={`fk-chat ${className}`} aria-label={label}>
      {lines.length === 0
        ? <div className="fk-chat__empty">{empty ?? <EmptyState glyph="◌" title="Nothing said yet" />}</div>
        : (
          <ol className="fk-chat__log" ref={logRef} data-testid={testIds.log} aria-live="polite" aria-label={`${label} log`}
            onScroll={(e) => { const el = e.currentTarget; pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24; }}>
            {lines.map((l) => l.system
              ? <li key={l.key} className="fk-chat__line fk-chat__line--system" data-testid="chat-line" data-system="true"><span className="fk-chat__body">{l.body}</span>{l.time && <time>{l.time}</time>}</li>
              : (
                <li key={l.key} className={`fk-chat__line ${l.me ? 'fk-chat__line--me' : ''}`} data-testid="chat-line">
                  <span className="fk-chat__head"><span className="fk-chat__who">{l.who}</span>{l.time && <time>{l.time}</time>}</span>
                  <span className="fk-chat__body">{l.body}</span>
                </li>
              ))}
          </ol>
        )}
      {onSend && (
        <div className="fk-chat__form">
          <Input data-testid={testIds.input} placeholder={placeholder} disabled={disabled} value={draft} maxLength={280} autoFocus={autoFocus}
            onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void send(); } e.stopPropagation(); }} aria-label={`${label} message`} />
          <Button primary disabled={disabled || !draft.trim()} loading={busy} onClick={() => void send()} data-testid={testIds.send}>Send</Button>
        </div>
      )}
    </div>
  );
}
